import { useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Title,
  Text,
  Badge,
  Group,
  Stack,
  Paper,
  Button,
  Loader,
  Select,
  Divider,
  Grid,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft } from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import dayjs from "dayjs";
import ObservationCommentsThread from "../../components/observations/ObservationCommentsThread";
import {
  OBSERVATION_SEVERITY_OPTIONS as SEVERITY_OPTIONS,
  observationSeverityColor,
  observationStatusColor,
  observationStatusLabel,
  type ObservationSeverity,
  type ObservationStatus,
} from "../../utils/observationStatus";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

interface ObservationDetail {
  id: string;
  reporterId: string;
  reporterName: string;
  reporterEmail: string;
  recordingDownloadUrl: string | null;
  recordingStartedAt: string | null;
  sessionRecordingId: string | null;
  parentObservationId: string | null;
  childSegments: Array<{
    id: string;
    recordingDownloadUrl: string | null;
    recordingStartedAt: string | null;
  }>;
  status: ObservationStatus;
  severity: ObservationSeverity | null;
  createdAt: string;
  updatedAt: string;
  displayCode: string | null;
}

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function MyObservationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const queryKey = ["my-observation", id];

  const { data, isLoading, error } = useQuery<ObservationDetail>({
    queryKey,
    queryFn: async () => {
      const res = await axios.get<{
        success: boolean;
        data: ObservationDetail;
      }>(`${API_BASE_URL}/api/my-observations/${id}`, {
        headers: getAuthHeaders(),
      });
      return res.data.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const updateSeverity = useMutation({
    mutationFn: async (severity: ObservationSeverity | null) => {
      await axios.patch(
        `${API_BASE_URL}/api/my-observations/${id}/severity`,
        { severity },
        {
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["my-observations"] });
      notifications.show({
        title: "Severity updated",
        message: "Your observation's priority has been updated.",
        color: "green",
      });
    },
    onError: (err) => {
      notifications.show({
        title: "Could not update severity",
        message: err instanceof Error ? err.message : "Unknown error",
        color: "red",
      });
    },
  });

  const handleSeverityChange = useCallback(
    (val: string | null) => {
      if (val === (data?.severity ?? null)) return;
      updateSeverity.mutate((val as ObservationSeverity | null) ?? null);
    },
    [data, updateSeverity]
  );

  const handleBack = useCallback(
    () => navigate("/my/observations"),
    [navigate]
  );

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader />
      </Stack>
    );
  }

  if (error || !data) {
    return (
      <Stack p="md">
        <Text c="red">
          Failed to load observation:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </Text>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={handleBack}
        >
          Back to My Observations
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" p="md">
      <Group justify="space-between" align="center">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Title order={2}>
            {data.displayCode
              ? `${data.displayCode} — Observation Detail`
              : "Observation Detail"}
          </Title>
          <Badge
            color={observationStatusColor(data.status)}
            variant="light"
            size="lg"
          >
            {observationStatusLabel(data.status)}
          </Badge>
          {data.severity && (
            <Badge
              color={observationSeverityColor(data.severity)}
              variant="filled"
              size="lg"
            >
              {data.severity}
            </Badge>
          )}
        </Group>

        <Select
          label="Priority"
          placeholder="Not set"
          clearable
          value={data.severity ?? null}
          onChange={handleSeverityChange}
          data={SEVERITY_OPTIONS}
          w={180}
          disabled={updateSeverity.isPending}
        />
      </Group>

      <Grid gutter="lg" align="flex-start">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <div style={{ position: "sticky", top: 16 }}>
            {data.recordingDownloadUrl ? (
              <Paper withBorder p="md">
                <Title order={4} mb="sm">
                  Screen Recording
                </Title>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  controls
                  src={data.recordingDownloadUrl}
                  style={{ width: "100%", maxHeight: 600, borderRadius: 8 }}
                />
              </Paper>
            ) : (
              data.childSegments.length === 0 && (
                <Paper withBorder p="md">
                  <Text c="dimmed" ta="center" py="xl">
                    No screen recordings available for this observation.
                  </Text>
                </Paper>
              )
            )}
          </div>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="lg">
            <Paper withBorder p="md">
              <Title order={4} mb="sm">
                Details
              </Title>
              <Stack gap="xs">
                <Group gap="xs">
                  <Text size="sm" fw={600}>
                    Status:
                  </Text>
                  <Badge
                    color={observationStatusColor(data.status)}
                    variant="light"
                  >
                    {observationStatusLabel(data.status)}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed">
                  Status is updated by the technicians.
                </Text>
                <Divider my="xs" />
                <Group gap="xs">
                  <Text size="sm" fw={600}>
                    Submitted:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {dayjs(data.createdAt).format("MMM D, YYYY h:mm A")}
                  </Text>
                </Group>
                <Group gap="xs">
                  <Text size="sm" fw={600}>
                    Last updated:
                  </Text>
                  <Text size="sm" c="dimmed">
                    {dayjs(data.updatedAt).format("MMM D, YYYY h:mm A")}
                  </Text>
                </Group>
              </Stack>
            </Paper>

            {data.childSegments.length > 0 && (
              <Paper withBorder p="md">
                <Title order={4} mb="sm">
                  Additional Segments ({data.childSegments.length})
                </Title>
                <Stack gap="md">
                  {data.childSegments.map((seg, idx) => (
                    <div key={seg.id}>
                      <Text size="sm" fw={500} mb="xs">
                        Segment {idx + 2}
                        {seg.recordingStartedAt &&
                          ` — ${dayjs(seg.recordingStartedAt).format(
                            "h:mm:ss A"
                          )}`}
                      </Text>
                      {seg.recordingDownloadUrl ? (
                        // eslint-disable-next-line jsx-a11y/media-has-caption
                        <video
                          controls
                          src={seg.recordingDownloadUrl}
                          style={{
                            width: "100%",
                            maxHeight: 300,
                            borderRadius: 8,
                          }}
                        />
                      ) : (
                        <Text size="sm" c="dimmed">
                          No recording available for this segment
                        </Text>
                      )}
                      {idx < data.childSegments.length - 1 && (
                        <Divider mt="md" />
                      )}
                    </div>
                  ))}
                </Stack>
              </Paper>
            )}

            <ObservationCommentsThread
              observationId={data.id}
              displayCode={data.displayCode}
            />
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
